import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { PageHeader } from '@/components/common/PageHeader'
import type { Tables } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type ProductFamily = Tables<'product_families'>
type Product = Tables<'products'>
type ProductOption = Tables<'product_options'>

export function AdminCatalogPage() {
  const [families, setFamilies] = useState<ProductFamily[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [options, setOptions] = useState<ProductOption[]>([])
  const [loading, setLoading] = useState(true)

  // Add forms
  const [showAddFamily, setShowAddFamily] = useState(false)
  const [showAddProduct, setShowAddProduct] = useState(false)
  const [newFamily, setNewFamily] = useState({ name: '', slug: '', description: '' })
  const [newProduct, setNewProduct] = useState({ name: '', sku: '', family_id: '', base_price: '', description: '', image_url: '', min_width_mm: '', max_width_mm: '', min_height_mm: '', max_height_mm: '', min_projection_mm: '', max_projection_mm: '', product_type: '', features: '', certifications: '', catalog_page: '' })

  // Edit state
  const [editingFamilyId, setEditingFamilyId] = useState<string | null>(null)
  const [editFamily, setEditFamily] = useState<Partial<ProductFamily>>({})
  const [editingProductId, setEditingProductId] = useState<string | null>(null)
  const [editProduct, setEditProduct] = useState<Record<string, string>>({})

  // Options state
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null)
  const [showAddOption, setShowAddOption] = useState(false)
  const [newOption, setNewOption] = useState({ group_name: '', name: '', sku_suffix: '', price_delta: '', is_default: false, sort_order: '0', in_stock: true, surcharge_percent: '' })
  const [editingOptionId, setEditingOptionId] = useState<string | null>(null)
  const [editOption, setEditOption] = useState<Record<string, string | boolean>>({})

  const load = async () => {
    const [famRes, prodRes, optRes] = await Promise.all([
      supabase.from('product_families').select('*').order('sort_order'),
      supabase.from('products').select('*').order('sort_order'),
      supabase.from('product_options').select('*').order('sort_order'),
    ])
    setFamilies(famRes.data || [])
    setProducts(prodRes.data || [])
    setOptions(optRes.data || [])
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  // Family CRUD
  const addFamily = async () => {
    await supabase.from('product_families').insert({
      name: newFamily.name,
      slug: newFamily.slug || newFamily.name.toLowerCase().replace(/\s+/g, '-'),
      description: newFamily.description || null,
    })
    setNewFamily({ name: '', slug: '', description: '' })
    setShowAddFamily(false)
    load()
  }

  const saveFamily = async () => {
    if (!editingFamilyId) return
    await supabase.from('product_families').update({
      name: editFamily.name,
      slug: editFamily.slug,
      description: editFamily.description || null,
      sort_order: editFamily.sort_order,
      is_active: editFamily.is_active,
    }).eq('id', editingFamilyId)
    setEditingFamilyId(null)
    load()
  }

  const toggleFamilyActive = async (f: ProductFamily) => {
    await supabase.from('product_families').update({ is_active: !f.is_active }).eq('id', f.id)
    setFamilies(families.map(x => x.id === f.id ? { ...x, is_active: !x.is_active } : x))
  }

  // Product CRUD
  const addProduct = async () => {
    await supabase.from('products').insert({
      name: newProduct.name,
      sku: newProduct.sku,
      family_id: newProduct.family_id,
      base_price: newProduct.base_price ? Number(newProduct.base_price) : null,
      description: newProduct.description || null,
      image_url: newProduct.image_url || null,
      product_type: newProduct.product_type || null,
      features: newProduct.features ? newProduct.features.split('\n').filter(Boolean) : null,
      certifications: newProduct.certifications ? newProduct.certifications.split('\n').filter(Boolean) : null,
      catalog_page: newProduct.catalog_page ? Number(newProduct.catalog_page) : null,
      min_width_mm: newProduct.min_width_mm ? Number(newProduct.min_width_mm) : null,
      max_width_mm: newProduct.max_width_mm ? Number(newProduct.max_width_mm) : null,
      min_height_mm: newProduct.min_height_mm ? Number(newProduct.min_height_mm) : null,
      max_height_mm: newProduct.max_height_mm ? Number(newProduct.max_height_mm) : null,
      min_projection_mm: newProduct.min_projection_mm ? Number(newProduct.min_projection_mm) : null,
      max_projection_mm: newProduct.max_projection_mm ? Number(newProduct.max_projection_mm) : null,
    })
    setNewProduct({ name: '', sku: '', family_id: '', base_price: '', description: '', image_url: '', min_width_mm: '', max_width_mm: '', min_height_mm: '', max_height_mm: '', min_projection_mm: '', max_projection_mm: '', product_type: '', features: '', certifications: '', catalog_page: '' })
    setShowAddProduct(false)
    load()
  }

  const saveProduct = async () => {
    if (!editingProductId) return
    await supabase.from('products').update({
      name: editProduct.name,
      sku: editProduct.sku,
      family_id: editProduct.family_id,
      base_price: editProduct.base_price ? Number(editProduct.base_price) : null,
      description: editProduct.description || null,
      image_url: editProduct.image_url || null,
      product_type: editProduct.product_type || null,
      features: editProduct.features ? editProduct.features.split('\n').filter(Boolean) : null,
      certifications: editProduct.certifications ? editProduct.certifications.split('\n').filter(Boolean) : null,
      catalog_page: editProduct.catalog_page ? Number(editProduct.catalog_page) : null,
      min_width_mm: editProduct.min_width_mm ? Number(editProduct.min_width_mm) : null,
      max_width_mm: editProduct.max_width_mm ? Number(editProduct.max_width_mm) : null,
      min_height_mm: editProduct.min_height_mm ? Number(editProduct.min_height_mm) : null,
      max_height_mm: editProduct.max_height_mm ? Number(editProduct.max_height_mm) : null,
      min_projection_mm: editProduct.min_projection_mm ? Number(editProduct.min_projection_mm) : null,
      max_projection_mm: editProduct.max_projection_mm ? Number(editProduct.max_projection_mm) : null,
      sort_order: editProduct.sort_order ? Number(editProduct.sort_order) : 0,
      is_active: editProduct.is_active === 'true',
    }).eq('id', editingProductId)
    setEditingProductId(null)
    load()
  }

  const toggleProductActive = async (p: Product) => {
    await supabase.from('products').update({ is_active: !p.is_active }).eq('id', p.id)
    setProducts(products.map(x => x.id === p.id ? { ...x, is_active: !x.is_active } : x))
  }

  // Option CRUD
  const selectedProduct = products.find(p => p.id === selectedProductId)
  const productOptions = options.filter(o => o.product_id === selectedProductId)
  const optionGroups = [...new Set(productOptions.map(o => o.group_name))]

  const addOption = async () => {
    if (!selectedProductId) return
    await supabase.from('product_options').insert({
      product_id: selectedProductId,
      group_name: newOption.group_name,
      name: newOption.name,
      sku_suffix: newOption.sku_suffix || null,
      price_delta: newOption.price_delta ? Number(newOption.price_delta) : null,
      is_default: newOption.is_default,
      sort_order: Number(newOption.sort_order) || 0,
      in_stock: newOption.in_stock,
      surcharge_percent: newOption.surcharge_percent ? Number(newOption.surcharge_percent) : null,
    })
    setNewOption({ group_name: '', name: '', sku_suffix: '', price_delta: '', is_default: false, sort_order: '0', in_stock: true, surcharge_percent: '' })
    setShowAddOption(false)
    load()
  }

  const saveOption = async () => {
    if (!editingOptionId) return
    await supabase.from('product_options').update({
      group_name: editOption.group_name as string,
      name: editOption.name as string,
      sku_suffix: (editOption.sku_suffix as string) || null,
      price_delta: editOption.price_delta ? Number(editOption.price_delta) : null,
      is_default: editOption.is_default === true || editOption.is_default === 'true',
      sort_order: Number(editOption.sort_order) || 0,
      in_stock: editOption.in_stock === true || editOption.in_stock === 'true',
      surcharge_percent: editOption.surcharge_percent ? Number(editOption.surcharge_percent) : null,
    }).eq('id', editingOptionId)
    setEditingOptionId(null)
    load()
  }

  const toggleOptionActive = async (o: ProductOption) => {
    await supabase.from('product_options').update({ is_active: !o.is_active }).eq('id', o.id)
    setOptions(options.map(x => x.id === o.id ? { ...x, is_active: !x.is_active } : x))
  }

  if (loading) return <p style={{ color: 'var(--color-text-muted)' }}>Loading catalog...</p>

  return (
    <div>
      <PageHeader title="Catalog Management" description="Manage product families, products, and options" />

      {/* Product Families */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Product Families ({families.length})</h2>
          <button className={css.btnPrimary} onClick={() => setShowAddFamily(!showAddFamily)}>Add Family</button>
        </div>

        {showAddFamily && (
          <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className={css.formGroup}><label className={css.formLabel}>Name *</label><input className={css.formInput} value={newFamily.name} onChange={e => setNewFamily({ ...newFamily, name: e.target.value })} /></div>
              <div className={css.formGroup}><label className={css.formLabel}>Slug</label><input className={css.formInput} value={newFamily.slug} onChange={e => setNewFamily({ ...newFamily, slug: e.target.value })} placeholder="auto-generated" /></div>
              <div className={css.formGroup}><label className={css.formLabel}>Description</label><input className={css.formInput} value={newFamily.description} onChange={e => setNewFamily({ ...newFamily, description: e.target.value })} /></div>
              <button className={css.btnPrimary} onClick={addFamily} disabled={!newFamily.name}>Save</button>
            </div>
          </div>
        )}

        {families.length > 0 && (
          <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
            <table className={css.table}>
              <thead><tr><th>Name</th><th>Slug</th><th>Products</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {families.map(f => (
                  <>{/* eslint-disable-next-line react/jsx-key */}
                    <tr key={f.id}>
                      <td style={{ fontWeight: 500 }}>{f.name}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{f.slug}</td>
                      <td>{products.filter(p => p.family_id === f.id).length}</td>
                      <td>{f.is_active ? 'Yes' : 'No'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                          <button className={`${css.btnGhost} ${css.btnSm}`} onClick={() => {
                            if (editingFamilyId === f.id) { setEditingFamilyId(null); return }
                            setEditingFamilyId(f.id)
                            setEditFamily({ name: f.name, slug: f.slug, description: f.description, sort_order: f.sort_order, is_active: f.is_active })
                          }}>{editingFamilyId === f.id ? 'Cancel' : 'Edit'}</button>
                          <button className={`${css.btnGhost} ${css.btnSm}`} style={{ color: f.is_active ? 'var(--color-error)' : 'var(--color-success)' }} onClick={() => toggleFamilyActive(f)}>
                            {f.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {editingFamilyId === f.id && (
                      <tr key={`${f.id}-edit`}>
                        <td colSpan={5} style={{ background: 'var(--color-bg-subtle)', padding: 'var(--space-5)' }}>
                          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                            <div className={css.formGroup}><label className={css.formLabel}>Name</label><input className={css.formInput} value={editFamily.name || ''} onChange={e => setEditFamily({ ...editFamily, name: e.target.value })} /></div>
                            <div className={css.formGroup}><label className={css.formLabel}>Slug</label><input className={css.formInput} value={editFamily.slug || ''} onChange={e => setEditFamily({ ...editFamily, slug: e.target.value })} /></div>
                            <div className={css.formGroup}><label className={css.formLabel}>Description</label><input className={css.formInput} value={editFamily.description || ''} onChange={e => setEditFamily({ ...editFamily, description: e.target.value })} /></div>
                            <div className={css.formGroup}><label className={css.formLabel}>Sort Order</label><input type="number" className={css.formInput} style={{ width: 80 }} value={editFamily.sort_order ?? 0} onChange={e => setEditFamily({ ...editFamily, sort_order: Number(e.target.value) })} /></div>
                            <button className={css.btnPrimary} onClick={saveFamily}>Save</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Products */}
      <div style={{ marginBottom: 'var(--space-8)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-lg)' }}>Products ({products.length})</h2>
          <button className={css.btnPrimary} onClick={() => setShowAddProduct(!showAddProduct)} disabled={families.length === 0}>Add Product</button>
        </div>

        {showAddProduct && (
          <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              <div className={css.formRow}>
                <div className={css.formGroup}><label className={css.formLabel}>Name *</label><input className={css.formInput} value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} /></div>
                <div className={css.formGroup}><label className={css.formLabel}>SKU *</label><input className={css.formInput} value={newProduct.sku} onChange={e => setNewProduct({ ...newProduct, sku: e.target.value })} /></div>
              </div>
              <div className={css.formRow}>
                <div className={css.formGroup}><label className={css.formLabel}>Family *</label><select className={css.formSelect} value={newProduct.family_id} onChange={e => setNewProduct({ ...newProduct, family_id: e.target.value })}><option value="">Select...</option>{families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                <div className={css.formGroup}><label className={css.formLabel}>Base Price (£)</label><input type="number" className={css.formInput} value={newProduct.base_price} onChange={e => setNewProduct({ ...newProduct, base_price: e.target.value })} /></div>
              </div>
              <div className={css.formRow}>
                <div className={css.formGroup}><label className={css.formLabel}>Image URL</label><input className={css.formInput} value={newProduct.image_url} onChange={e => setNewProduct({ ...newProduct, image_url: e.target.value })} placeholder="https://..." /></div>
                <div className={css.formGroup}><label className={css.formLabel}>Product Type</label><input className={css.formInput} value={newProduct.product_type} onChange={e => setNewProduct({ ...newProduct, product_type: e.target.value })} placeholder="e.g. cassette_awning" /></div>
              </div>
              <div className={css.formRow}>
                <div className={css.formGroup}><label className={css.formLabel}>Catalog Page</label><input type="number" className={css.formInput} value={newProduct.catalog_page} onChange={e => setNewProduct({ ...newProduct, catalog_page: e.target.value })} /></div>
              </div>
              <div className={css.formRow}>
                <div className={css.formGroup}><label className={css.formLabel}>Min Width (mm)</label><input type="number" className={css.formInput} value={newProduct.min_width_mm} onChange={e => setNewProduct({ ...newProduct, min_width_mm: e.target.value })} /></div>
                <div className={css.formGroup}><label className={css.formLabel}>Max Width (mm)</label><input type="number" className={css.formInput} value={newProduct.max_width_mm} onChange={e => setNewProduct({ ...newProduct, max_width_mm: e.target.value })} /></div>
              </div>
              <div className={css.formRow}>
                <div className={css.formGroup}><label className={css.formLabel}>Min Height (mm)</label><input type="number" className={css.formInput} value={newProduct.min_height_mm} onChange={e => setNewProduct({ ...newProduct, min_height_mm: e.target.value })} /></div>
                <div className={css.formGroup}><label className={css.formLabel}>Max Height (mm)</label><input type="number" className={css.formInput} value={newProduct.max_height_mm} onChange={e => setNewProduct({ ...newProduct, max_height_mm: e.target.value })} /></div>
              </div>
              <div className={css.formRow}>
                <div className={css.formGroup}><label className={css.formLabel}>Min Projection (mm)</label><input type="number" className={css.formInput} value={newProduct.min_projection_mm} onChange={e => setNewProduct({ ...newProduct, min_projection_mm: e.target.value })} /></div>
                <div className={css.formGroup}><label className={css.formLabel}>Max Projection (mm)</label><input type="number" className={css.formInput} value={newProduct.max_projection_mm} onChange={e => setNewProduct({ ...newProduct, max_projection_mm: e.target.value })} /></div>
              </div>
              <div className={css.formGroup}><label className={css.formLabel}>Description</label><textarea className={css.formTextarea} value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} /></div>
              <div className={css.formGroup}><label className={css.formLabel}>Features (one per line)</label><textarea className={css.formTextarea} value={newProduct.features} onChange={e => setNewProduct({ ...newProduct, features: e.target.value })} placeholder="Feature 1&#10;Feature 2" /></div>
              <div className={css.formGroup}><label className={css.formLabel}>Certifications (one per line)</label><textarea className={css.formTextarea} value={newProduct.certifications} onChange={e => setNewProduct({ ...newProduct, certifications: e.target.value })} placeholder="LED&#10;UNI EN 13561:2015" /></div>
              <button className={css.btnPrimary} onClick={addProduct} disabled={!newProduct.name || !newProduct.sku || !newProduct.family_id} style={{ alignSelf: 'flex-start' }}>Save Product</button>
            </div>
          </div>
        )}

        {products.length > 0 && (
          <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
            <table className={css.table}>
              <thead><tr><th>SKU</th><th>Name</th><th>Family</th><th>Base Price</th><th>Dimensions</th><th>Active</th><th>Actions</th></tr></thead>
              <tbody>
                {products.map(p => {
                  const fam = families.find(f => f.id === p.family_id)
                  return (
                    <>{/* eslint-disable-next-line react/jsx-key */}
                      <tr key={p.id}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{p.sku}</td>
                        <td style={{ fontWeight: 500 }}>{p.name}</td>
                        <td>{fam?.name || '—'}</td>
                        <td>{p.base_price ? `£${Number(p.base_price).toLocaleString()}` : '—'}</td>
                        <td style={{ fontSize: 'var(--text-xs)' }}>{p.min_width_mm ? `${p.min_width_mm}–${p.max_width_mm} x ${p.min_height_mm}–${p.max_height_mm}mm` : '—'}</td>
                        <td>{p.is_active ? 'Yes' : 'No'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <button className={`${css.btnGhost} ${css.btnSm}`} onClick={() => {
                              if (editingProductId === p.id) { setEditingProductId(null); return }
                              setEditingProductId(p.id)
                              setEditProduct({
                                name: p.name, sku: p.sku, family_id: p.family_id,
                                base_price: p.base_price?.toString() || '', description: p.description || '',
                                image_url: p.image_url || '',
                                product_type: p.product_type || '', features: p.features?.join('\n') || '',
                                certifications: p.certifications?.join('\n') || '', catalog_page: p.catalog_page?.toString() || '',
                                min_width_mm: p.min_width_mm?.toString() || '', max_width_mm: p.max_width_mm?.toString() || '',
                                min_height_mm: p.min_height_mm?.toString() || '', max_height_mm: p.max_height_mm?.toString() || '',
                                min_projection_mm: p.min_projection_mm?.toString() || '', max_projection_mm: p.max_projection_mm?.toString() || '',
                                sort_order: p.sort_order.toString(), is_active: p.is_active.toString(),
                              })
                            }}>{editingProductId === p.id ? 'Cancel' : 'Edit'}</button>
                            <button className={`${css.btnGhost} ${css.btnSm}`} onClick={() => {
                              setSelectedProductId(selectedProductId === p.id ? null : p.id)
                              setShowAddOption(false)
                              setEditingOptionId(null)
                            }} style={{ color: selectedProductId === p.id ? 'var(--color-primary)' : undefined }}>
                              Options ({options.filter(o => o.product_id === p.id).length})
                            </button>
                            <button className={`${css.btnGhost} ${css.btnSm}`} style={{ color: p.is_active ? 'var(--color-error)' : 'var(--color-success)' }} onClick={() => toggleProductActive(p)}>
                              {p.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editingProductId === p.id && (
                        <tr key={`${p.id}-edit`}>
                          <td colSpan={7} style={{ background: 'var(--color-bg-subtle)', padding: 'var(--space-5)' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Name</label><input className={css.formInput} value={editProduct.name} onChange={e => setEditProduct({ ...editProduct, name: e.target.value })} /></div>
                                <div className={css.formGroup}><label className={css.formLabel}>SKU</label><input className={css.formInput} value={editProduct.sku} onChange={e => setEditProduct({ ...editProduct, sku: e.target.value })} /></div>
                              </div>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Family</label><select className={css.formSelect} value={editProduct.family_id} onChange={e => setEditProduct({ ...editProduct, family_id: e.target.value })}>{families.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}</select></div>
                                <div className={css.formGroup}><label className={css.formLabel}>Base Price (£)</label><input type="number" className={css.formInput} value={editProduct.base_price} onChange={e => setEditProduct({ ...editProduct, base_price: e.target.value })} /></div>
                              </div>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Image URL</label><input className={css.formInput} value={editProduct.image_url} onChange={e => setEditProduct({ ...editProduct, image_url: e.target.value })} /></div>
                                <div className={css.formGroup}><label className={css.formLabel}>Product Type</label><input className={css.formInput} value={editProduct.product_type} onChange={e => setEditProduct({ ...editProduct, product_type: e.target.value })} /></div>
                              </div>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Catalog Page</label><input type="number" className={css.formInput} value={editProduct.catalog_page} onChange={e => setEditProduct({ ...editProduct, catalog_page: e.target.value })} /></div>
                              </div>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Min Width (mm)</label><input type="number" className={css.formInput} value={editProduct.min_width_mm} onChange={e => setEditProduct({ ...editProduct, min_width_mm: e.target.value })} /></div>
                                <div className={css.formGroup}><label className={css.formLabel}>Max Width (mm)</label><input type="number" className={css.formInput} value={editProduct.max_width_mm} onChange={e => setEditProduct({ ...editProduct, max_width_mm: e.target.value })} /></div>
                              </div>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Min Height (mm)</label><input type="number" className={css.formInput} value={editProduct.min_height_mm} onChange={e => setEditProduct({ ...editProduct, min_height_mm: e.target.value })} /></div>
                                <div className={css.formGroup}><label className={css.formLabel}>Max Height (mm)</label><input type="number" className={css.formInput} value={editProduct.max_height_mm} onChange={e => setEditProduct({ ...editProduct, max_height_mm: e.target.value })} /></div>
                              </div>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Min Projection (mm)</label><input type="number" className={css.formInput} value={editProduct.min_projection_mm} onChange={e => setEditProduct({ ...editProduct, min_projection_mm: e.target.value })} /></div>
                                <div className={css.formGroup}><label className={css.formLabel}>Max Projection (mm)</label><input type="number" className={css.formInput} value={editProduct.max_projection_mm} onChange={e => setEditProduct({ ...editProduct, max_projection_mm: e.target.value })} /></div>
                              </div>
                              <div className={css.formRow}>
                                <div className={css.formGroup}><label className={css.formLabel}>Sort Order</label><input type="number" className={css.formInput} value={editProduct.sort_order} onChange={e => setEditProduct({ ...editProduct, sort_order: e.target.value })} /></div>
                                <div className={css.formGroup}><label className={css.formLabel}>Active</label><select className={css.formSelect} value={editProduct.is_active} onChange={e => setEditProduct({ ...editProduct, is_active: e.target.value })}><option value="true">Yes</option><option value="false">No</option></select></div>
                              </div>
                              <div className={css.formGroup}><label className={css.formLabel}>Description</label><textarea className={css.formTextarea} value={editProduct.description} onChange={e => setEditProduct({ ...editProduct, description: e.target.value })} /></div>
                              <div className={css.formGroup}><label className={css.formLabel}>Features (one per line)</label><textarea className={css.formTextarea} value={editProduct.features} onChange={e => setEditProduct({ ...editProduct, features: e.target.value })} /></div>
                              <div className={css.formGroup}><label className={css.formLabel}>Certifications (one per line)</label><textarea className={css.formTextarea} value={editProduct.certifications} onChange={e => setEditProduct({ ...editProduct, certifications: e.target.value })} /></div>
                              <button className={css.btnPrimary} onClick={saveProduct} style={{ alignSelf: 'flex-start' }}>Save Product</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Product Options */}
      {selectedProductId && selectedProduct && (
        <div style={{ marginBottom: 'var(--space-8)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
            <h2 style={{ fontSize: 'var(--text-lg)' }}>Options for {selectedProduct.name} ({productOptions.length})</h2>
            <button className={css.btnPrimary} onClick={() => setShowAddOption(!showAddOption)}>Add Option</button>
          </div>

          {showAddOption && (
            <div className={css.card} style={{ marginBottom: 'var(--space-4)' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                <div className={css.formRow}>
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Group Name *</label>
                    <input className={css.formInput} value={newOption.group_name} onChange={e => setNewOption({ ...newOption, group_name: e.target.value })} list="option-groups" placeholder="e.g. Color, Motor Type" />
                    <datalist id="option-groups">{optionGroups.map(g => <option key={g} value={g} />)}</datalist>
                  </div>
                  <div className={css.formGroup}><label className={css.formLabel}>Name *</label><input className={css.formInput} value={newOption.name} onChange={e => setNewOption({ ...newOption, name: e.target.value })} placeholder="e.g. Red, 24V DC" /></div>
                </div>
                <div className={css.formRow}>
                  <div className={css.formGroup}><label className={css.formLabel}>SKU Suffix</label><input className={css.formInput} value={newOption.sku_suffix} onChange={e => setNewOption({ ...newOption, sku_suffix: e.target.value })} /></div>
                  <div className={css.formGroup}><label className={css.formLabel}>Price Delta (£)</label><input type="number" className={css.formInput} value={newOption.price_delta} onChange={e => setNewOption({ ...newOption, price_delta: e.target.value })} /></div>
                </div>
                <div className={css.formRow}>
                  <div className={css.formGroup}><label className={css.formLabel}>Surcharge %</label><input type="number" className={css.formInput} value={newOption.surcharge_percent} onChange={e => setNewOption({ ...newOption, surcharge_percent: e.target.value })} placeholder="e.g. 10" /></div>
                  <div className={css.formGroup}><label className={css.formLabel}>Sort Order</label><input type="number" className={css.formInput} value={newOption.sort_order} onChange={e => setNewOption({ ...newOption, sort_order: e.target.value })} /></div>
                </div>
                <div className={css.formRow}>
                  <div className={css.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
                      <input type="checkbox" checked={newOption.in_stock} onChange={e => setNewOption({ ...newOption, in_stock: e.target.checked })} /> In stock
                    </label>
                  </div>
                  <div className={css.formGroup}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
                      <input type="checkbox" checked={newOption.is_default} onChange={e => setNewOption({ ...newOption, is_default: e.target.checked })} /> Default option
                    </label>
                  </div>
                </div>
                <button className={css.btnPrimary} onClick={addOption} disabled={!newOption.group_name || !newOption.name} style={{ alignSelf: 'flex-start' }}>Save Option</button>
              </div>
            </div>
          )}

          {productOptions.length > 0 ? (
            <div className={css.card} style={{ padding: 0, overflow: 'hidden' }}>
              <table className={css.table}>
                <thead><tr><th>Group</th><th>Name</th><th>SKU Suffix</th><th>Price Delta</th><th>In Stock</th><th>Surcharge</th><th>Default</th><th>Active</th><th>Sort</th><th>Actions</th></tr></thead>
                <tbody>
                  {productOptions.map(o => (
                    <>{/* eslint-disable-next-line react/jsx-key */}
                      <tr key={o.id}>
                        <td style={{ fontWeight: 500 }}>{o.group_name}</td>
                        <td>{o.name}</td>
                        <td style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)' }}>{o.sku_suffix || '—'}</td>
                        <td>{o.price_delta ? `+£${Number(o.price_delta).toLocaleString()}` : '—'}</td>
                        <td style={{ color: o.in_stock ? undefined : 'var(--color-technical)' }}>{o.in_stock ? 'Yes' : 'No'}</td>
                        <td>{o.surcharge_percent ? `${o.surcharge_percent}%` : '—'}</td>
                        <td>{o.is_default ? 'Yes' : 'No'}</td>
                        <td>{o.is_active ? 'Yes' : 'No'}</td>
                        <td>{o.sort_order}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                            <button className={`${css.btnGhost} ${css.btnSm}`} onClick={() => {
                              if (editingOptionId === o.id) { setEditingOptionId(null); return }
                              setEditingOptionId(o.id)
                              setEditOption({
                                group_name: o.group_name, name: o.name,
                                sku_suffix: o.sku_suffix || '', price_delta: o.price_delta?.toString() || '',
                                is_default: o.is_default, sort_order: o.sort_order.toString(),
                                in_stock: o.in_stock, surcharge_percent: o.surcharge_percent?.toString() || '',
                              })
                            }}>{editingOptionId === o.id ? 'Cancel' : 'Edit'}</button>
                            <button className={`${css.btnGhost} ${css.btnSm}`} style={{ color: o.is_active ? 'var(--color-error)' : 'var(--color-success)' }} onClick={() => toggleOptionActive(o)}>
                              {o.is_active ? 'Deactivate' : 'Activate'}
                            </button>
                          </div>
                        </td>
                      </tr>
                      {editingOptionId === o.id && (
                        <tr key={`${o.id}-edit`}>
                          <td colSpan={10} style={{ background: 'var(--color-bg-subtle)', padding: 'var(--space-5)' }}>
                            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                              <div className={css.formGroup}><label className={css.formLabel}>Group</label><input className={css.formInput} value={editOption.group_name as string} onChange={e => setEditOption({ ...editOption, group_name: e.target.value })} /></div>
                              <div className={css.formGroup}><label className={css.formLabel}>Name</label><input className={css.formInput} value={editOption.name as string} onChange={e => setEditOption({ ...editOption, name: e.target.value })} /></div>
                              <div className={css.formGroup}><label className={css.formLabel}>SKU Suffix</label><input className={css.formInput} value={editOption.sku_suffix as string} onChange={e => setEditOption({ ...editOption, sku_suffix: e.target.value })} /></div>
                              <div className={css.formGroup}><label className={css.formLabel}>Price Delta (£)</label><input type="number" className={css.formInput} style={{ width: 100 }} value={editOption.price_delta as string} onChange={e => setEditOption({ ...editOption, price_delta: e.target.value })} /></div>
                              <div className={css.formGroup}><label className={css.formLabel}>Surcharge %</label><input type="number" className={css.formInput} style={{ width: 80 }} value={editOption.surcharge_percent as string} onChange={e => setEditOption({ ...editOption, surcharge_percent: e.target.value })} /></div>
                              <div className={css.formGroup}><label className={css.formLabel}>Sort</label><input type="number" className={css.formInput} style={{ width: 70 }} value={editOption.sort_order as string} onChange={e => setEditOption({ ...editOption, sort_order: e.target.value })} /></div>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', paddingBottom: 'var(--space-3)' }}>
                                <input type="checkbox" checked={editOption.in_stock === true || editOption.in_stock === 'true'} onChange={e => setEditOption({ ...editOption, in_stock: e.target.checked })} /> In Stock
                              </label>
                              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', paddingBottom: 'var(--space-3)' }}>
                                <input type="checkbox" checked={editOption.is_default === true || editOption.is_default === 'true'} onChange={e => setEditOption({ ...editOption, is_default: e.target.checked })} /> Default
                              </label>
                              <button className={css.btnPrimary} onClick={saveOption}>Save</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={css.card} style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>
              No options configured for this product. Click "Add Option" to get started.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
